import { useState, useEffect, useRef, useCallback } from 'react';

interface UseIdleOptions {
  timeout?: number;
  events?: string[];
}

const DEFAULT_EVENTS = [
  'click',
  'scroll',
  'touchstart',
  'touchmove',
  'mousemove',
  'keypress',
];

export function useIdle({
  timeout = 60000,
  events = DEFAULT_EVENTS,
}: UseIdleOptions = {}): [boolean, () => void] {
  const [isIdle, setIsIdle] = useState(false);
  const timerIdRef = useRef<number | undefined>(undefined);
  const timeoutRef = useRef(timeout);

  useEffect(() => {
    timeoutRef.current = timeout;
  }, [timeout]);

  const startTimer = useCallback(() => {
    if (timerIdRef.current) {
      window.clearTimeout(timerIdRef.current);
    }

    timerIdRef.current = window.setTimeout(() => {
      setIsIdle(true);
    }, timeoutRef.current);
  }, []);

  const resetTimer = useCallback(() => {
    setIsIdle(false);
    startTimer();
  }, [startTimer]);

  const handleActivity = useCallback(() => {
    if (timerIdRef.current) {
      window.clearTimeout(timerIdRef.current);
    }
    startTimer();
  }, [startTimer]);

  useEffect(() => {
    events.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    startTimer();

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      if (timerIdRef.current) {
        window.clearTimeout(timerIdRef.current);
      }
    };
  }, [events, handleActivity, startTimer]);

  return [isIdle, resetTimer];
}
